
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import * as action from '../Action/VerifyToken/VerifyToken_Action';
import { login } from '../Action/Login/Login_Action';
import * as site_action from '../Action/SiteDetails/SiteDetails_Action';
import * as Authservices from '../Services/services';
import { isSuperAdminRole } from '../config/permissions';

const AuthContext = createContext();

export const CHANGE_PASSWORD_PATH = '/change-password';

/**
 * ─── Post-onboarding maintenance exemption ───────────────────────────────────
 * An admin who has just cleared the forced change-password gate is let into the
 * panel for the rest of the session even while Admin Panel Maintenance is on.
 *
 * Without this, onboarding dead-ends: Super Admin creates the account, the new
 * admin signs in with the temporary password, sets their own — and the instant
 * the gate lifts the maintenance redirect (previously masked by that same gate)
 * drops them on /maintenance, with no way forward and no way to ask anyone but
 * the Super Admin, who is exempt and therefore never sees the problem.
 *
 * Session-scoped on purpose: it is cleared on logout and on every fresh sign-in,
 * so it grants exactly one session and never becomes a standing bypass.
 */
const MAINTENANCE_EXEMPT_KEY = 'post_password_change_exempt';

export const grantMaintenanceExemption = () => {
    try {
        sessionStorage.setItem(MAINTENANCE_EXEMPT_KEY, '1');
    } catch (e) {
        // Private-mode / storage-disabled: fall back to no exemption.
    }
};

export const hasMaintenanceExemption = () => {
    try {
        return sessionStorage.getItem(MAINTENANCE_EXEMPT_KEY) === '1';
    } catch (e) {
        return false;
    }
};

export const clearMaintenanceExemption = () => {
    try {
        sessionStorage.removeItem(MAINTENANCE_EXEMPT_KEY);
    } catch (e) {
        // Nothing to clear if storage is unavailable.
    }
};

/** Pulls the admin object out of whichever envelope the API used. */
const extractAdmin = (response) =>
    response?.data?.admin ||
    response?.responseDetails?.admin ||
    response?.admin ||
    response?.responseDetails ||
    response ||
    {};

const needsPasswordChange = (admin) =>
    admin?.must_change_password === true ||
    admin?.must_change_password === 1 ||
    admin?.must_change_password === '1';

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    // The last route we re-read the profile for. A plain "have we verified yet"
    // flag only ever revalidated once per mount, so a role change or a
    // deactivation was invisible to the UI until a full page reload — every
    // client-side navigation kept rendering off the permissions cached at
    // login (CI-566). Keyed by path, each navigation revalidates instead.
    const lastVerifiedPath = useRef(null);
    const [token, setToken] = useState(() => {
        return localStorage.getItem('token') || null;
    });
    const [user, setUser] = useState(() => {
        try {
            const admin_details = localStorage.getItem('admin_details');
            if (admin_details && admin_details !== 'undefined') return JSON.parse(admin_details);
        } catch (e) {
            localStorage.removeItem('admin_details');
        }
        return {};
    });
    const [checkLicense, setCheckLicense] = useState(null);
    const [siteData, setSiteData] = useState({});
    // Distinguishes "site settings not fetched yet" from "fetched, maintenance
    // is off". Without it the maintenance gate can't tell the two apart and has
    // to assume off, which flashes the dashboard before the real answer lands.
    const [siteDataLoaded, setSiteDataLoaded] = useState(false);

    useEffect(() => {
        const verifyLicense = async () => {
            setCheckLicense(true);
            localStorage.setItem('checkLicense', true);
            if (location.pathname === '/license') {
                navigate('/');
            }
        };

        if (!checkLicense) {
            verifyLicense();
        }
    }, [dispatch, checkLicense, location, navigate]);

    const userLogin = useCallback((data, onError) => {
        // A new sign-in never inherits the previous session's exemption — only
        // clearing the change-password gate in *this* session can grant one.
        clearMaintenanceExemption();
        dispatch(login(data)).then(async (response) => {
            let accessToken =
                response?.data?.token?.accessToken ||
                response?.responseDetails?.token?.accessToken ||
                response?.token?.accessToken ||
                response?.accessToken ||
                response?.token ||
                response?.data?.token ||
                response?.responseDetails?.token;
            let adminDetails = extractAdmin(response);
            const refreshToken =
                response?.data?.refreshToken ||
                response?.responseDetails?.refreshToken ||
                response?.refreshToken;
            if (!accessToken) {
                console.error('Could not extract token from login response:', JSON.stringify(response));
                const msg = 'Login failed: unexpected response format.';
                toast.error(msg);
                if (onError) onError(msg);
                return;
            }
            localStorage.setItem('token', accessToken);
            if (refreshToken) {
                localStorage.setItem('refresh_token', refreshToken);
            }

            // signin's admin object comes off the shared users table and can
            // carry unrelated fields (e.g. a mobile-app `login_type`) that the
            // canonical get-profile response doesn't. Reading those through
            // AuthContext.user right after login — before anything ever calls
            // get-profile — trips legacy `login_type === 'Guest'` checks (the
            // admin-panel maintenance gate among them) for a real admin, for
            // exactly one page load. Canonicalizing here, using the same
            // source refreshProfile() uses everywhere else, makes the very
            // first render after login identical to the state after a refresh.
            try {
                const res = await Authservices.getProfile();
                const canonical = extractAdmin(res?.data);
                if (canonical && Object.keys(canonical).length > 0) {
                    adminDetails = canonical;
                }
            } catch (err) {
                console.warn('Could not canonicalize admin profile after login, using signin payload:', err?.message);
            }

            if (adminDetails) {
                localStorage.setItem('admin_details', JSON.stringify(adminDetails));
            }
            setToken(accessToken);
            setUser(adminDetails || {});
            // Signin already canonicalized the profile via get-profile above, so
            // the landing route does not need to fetch it a second time.
            lastVerifiedPath.current = '/';

            // Forced change-password gate: go straight there so no nav or section
            // ever renders, not even for a frame. The backend blocks everything
            // else anyway, but the UI must not flash the wrong screen first.
            if (needsPasswordChange(adminDetails)) {
                navigate(CHANGE_PASSWORD_PATH, { replace: true });
                return;
            }

            dispatch(site_action.getSiteData()).then((response) => {
                if (response?.responseDetails) {
                    setSiteData(response.responseDetails);
                }
            }).catch(() => {});
            navigate('/');
        }).catch(error => {
            console.error('Login error:', error);
            const msg = error?.responseMessage || error?.message || 'Login failed. Please check your connection.';
            toast.error(msg);
            if (onError) onError(msg);
        })
    }, [dispatch, navigate]);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('admin_details');
        localStorage.removeItem('refresh_token');
        clearMaintenanceExemption();
        lastVerifiedPath.current = null;
        setToken(null);
        setUser({});
        navigate('/login');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    /**
     * Re-reads the admin (role, permissions, permissions_version,
     * must_change_password) from get-profile — one of the only two routes still
     * reachable while the change-password gate is set, so it also works as the
     * "am I still gated?" check on app load.
     */
    const refreshProfile = useCallback(async () => {
        const res = await Authservices.getProfile();
        const data = extractAdmin(res?.data);
        localStorage.setItem('admin_details', JSON.stringify(data));
        setUser(data);
        return data;
    }, []);

    const fetchUser = useCallback(async () => {
        if (!token) return;
        try {
            const data = await refreshProfile();
            if (needsPasswordChange(data) && location.pathname !== CHANGE_PASSWORD_PATH) {
                navigate(CHANGE_PASSWORD_PATH, { replace: true });
            }
        } catch (error) {
            const status = error?.response?.status;
            const errorType = error?.response?.data?.errorType;

            // The gate 403s everything else; the interceptor already redirects.
            if (status === 403 && errorType === 'PasswordChangeRequiredError') {
                return;
            }
            if (status === 401) {
                logout();
                return;
            }

            // get-profile unavailable for another reason — fall back to the
            // legacy verify-token check rather than dropping the session.
            dispatch(action.verifyToken({ 'token': token })).then((response) => {
                const data = extractAdmin(response);
                localStorage.setItem('admin_details', JSON.stringify(data));
                setUser(data);
            }).catch(err => {
                const s = err?.response?.status || err?.status;
                if (s === 401) {
                    logout();
                } else {
                    console.warn('Session verification failed, keeping session:', err?.message);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, token, logout, refreshProfile, navigate, location.pathname]);

    const fetchSiteData = useCallback(async () => {
        try {
            dispatch(site_action.getSiteData()).then((response) => {
                if (response?.responseDetails) {
                    setSiteData(response.responseDetails);
                } else {
                    setSiteData({ title: 'HikeCinema Admin', favicon: null, is_admin_maintenance: 0 });
                }
            }).catch(error => {
                console.warn("Failed to fetch site data, using defaults:", error?.message);
                setSiteData({ title: 'HikeCinema Admin', favicon: null, is_admin_maintenance: 0 });
            }).finally(() => {
                // Settled either way — the gate must never wait forever on a
                // failed request, or a site-details outage locks the panel.
                setSiteDataLoaded(true);
            })
        } catch (err) {
            console.warn("Error in fetchSiteData:", err?.message);
            setSiteData({ title: 'HikeCinema Admin', favicon: null, is_admin_maintenance: 0 });
            setSiteDataLoaded(true);
        }
    }, [dispatch]);

    useEffect(() => {
        const publicPaths = ['/login', '/forget-password', '/reset-password', '/license', '/maintenance'];

        if (publicPaths.includes(location.pathname)) {
            return;
        }

        // /change-password still needs a token, but must not trigger a profile
        // re-fetch loop while the gate is active — the screen loads it itself.
        if (location.pathname === CHANGE_PASSWORD_PATH) {
            if (!token) navigate('/login');
            return;
        }

        if (token) {
            // Re-read the profile on every route change, not just the first.
            // get-profile 401s once permissions_version has been bumped (role
            // edited, reassigned or revoked) and 403s a deactivated account, so
            // this is what turns a server-side revocation into an actual
            // sign-out instead of a stale UI that only the data calls notice.
            if (lastVerifiedPath.current !== location.pathname) {
                lastVerifiedPath.current = location.pathname;
                fetchUser();
            }
        } else {
            navigate('/login');
        }
    }, [location.pathname, token, fetchUser, navigate]);

    useEffect(() => {
        fetchSiteData();
    }, [fetchSiteData]);

    useEffect(() => {
        const handleStorageChange = () => {
            const storedToken = localStorage.getItem('token');
            if (storedToken !== token) {
                setToken(storedToken);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [token]);

    const mustChangePassword = needsPasswordChange(user);
    const isSuperAdmin = isSuperAdminRole(user?.role);

    return (
        <AuthContext.Provider value={{
            token,
            user,
            setUser,
            userLogin,
            logout,
            siteData,
            siteDataLoaded,
            refreshProfile,
            mustChangePassword,
            isSuperAdmin,
            permissionsVersion: user?.permissions_version ?? null,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
